import React, { useEffect, useRef, useState } from "react";
import { Search, MapPin } from "lucide-react";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { createStyledMap, LIMA_CENTER } from "./mapTheme";

/**
 * Punto del pasajero con mapa real (Maps JS API + Places Autocomplete) y
 * pin arrastrable con reverse geocoding. Si Maps no está configurado (o
 * falla la carga), cae a los mismos inputs de texto libre que ya existían
 * -- nunca pantalla en blanco (ver CLAUDE.md 2c y useGoogleMaps()).
 *
 * `onChange` recibe un patch parcial ({ address } o { address, lat, lng })
 * -- el llamador lo mergea en su propio estado de formulario.
 */
const PassengerPointPicker = ({ label, address, reference, lat, lng, onChange, testId = "passenger-point" }) => {
  const { status } = useGoogleMaps();
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markerRef = useRef(null);
  const searchInputRef = useRef(null);
  const geocoderRef = useRef(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (status !== "ready" || !mapContainerRef.current || mapRef.current) return;

    const center = lat != null && lng != null ? { lat, lng } : LIMA_CENTER;
    const map = createStyledMap(mapContainerRef.current, { center, zoom: lat != null ? 16 : 12 });
    const marker = new window.google.maps.Marker({
      position: center,
      map,
      draggable: true,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#C9A227",
        fillOpacity: 1,
        strokeColor: "#0D0E10",
        strokeWeight: 2,
      },
    });
    geocoderRef.current = new window.google.maps.Geocoder();

    marker.addListener("dragend", () => {
      const position = marker.getPosition();
      reverseGeocode(position.lat(), position.lng());
    });

    if (searchInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
        componentRestrictions: { country: "pe" },
        fields: ["geometry", "formatted_address"],
      });
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.geometry?.location) return;
        const position = place.geometry.location;
        map.panTo(position);
        map.setZoom(16);
        marker.setPosition(position);
        onChange({
          address: place.formatted_address || searchInputRef.current.value,
          lat: position.lat(),
          lng: position.lng(),
        });
      });
    }

    mapRef.current = map;
    markerRef.current = marker;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const reverseGeocode = (newLat, newLng) => {
    setResolving(true);
    geocoderRef.current.geocode({ location: { lat: newLat, lng: newLng } }, (results, geocodeStatus) => {
      setResolving(false);
      const resolvedAddress = geocodeStatus === "OK" && results?.[0] ? results[0].formatted_address : address;
      onChange({ address: resolvedAddress, lat: newLat, lng: newLng });
    });
  };

  if (status === "loading") {
    return (
      <div className="h-48 flex items-center justify-center border border-[#2E2F33] rounded-lg bg-[#0D0E10] text-xs text-[#A0A0A5] font-body" data-testid={`${testId}-loading`}>
        Cargando mapa…
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div data-testid={`${testId}-fallback`}>
        <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">{label}</label>
        <input
          value={address}
          onChange={(e) => onChange({ address: e.target.value })}
          className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none"
          placeholder="Escribe la dirección"
          data-testid={`${testId}-address-input`}
        />
        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">Referencia adicional</label>
          <input
            value={reference || ""}
            onChange={(e) => onChange({ reference: e.target.value })}
            className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none"
            data-testid={`${testId}-reference-input`}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`${testId}-map`}>
      <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">{label}</label>
      <div className="bg-[#1C1D20] border border-[#2E2F33] focus-within:border-[#C9A227] rounded-lg px-4 py-3 flex items-center gap-3 mb-3">
        <Search size={14} className="text-[#C9A227]" />
        <input
          ref={searchInputRef}
          defaultValue={address}
          className="flex-1 bg-transparent outline-none text-sm text-[#F5F5F5] font-body"
          placeholder="Buscar dirección…"
          data-testid={`${testId}-search-input`}
        />
      </div>
      <div ref={mapContainerRef} className="w-full h-64 rounded-xl overflow-hidden border border-[#2E2F33]" data-testid={`${testId}-canvas`} />
      <p className="mt-2 text-xs text-[#A0A0A5] font-body flex items-start gap-1.5">
        <MapPin size={12} className="text-[#C9A227] mt-0.5 shrink-0" />
        {resolving ? "Ubicando dirección…" : address || "Arrastra el pin o busca una dirección"}
      </p>
      <div className="mt-4">
        <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">Referencia adicional</label>
        <input
          value={reference || ""}
          onChange={(e) => onChange({ reference: e.target.value })}
          className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none"
          data-testid={`${testId}-reference-input`}
        />
      </div>
    </div>
  );
};

export default PassengerPointPicker;
